import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useSpring, animated, easings } from '@react-spring/web';
import { ReactSVG } from 'react-svg';
import { Textfit } from 'react-textfit';
import { kidInstrumentAssets } from '../../../../utils/RoundUpUtils';
import logo from '../../assets/logo.svg';
import sunRays from '../../assets/backgrounds/sun_rays.svg';
import '../Slides.scss';
import './IntroSlide.scss';

const IntroSlide = ({ data, startFunc }) => {
  const { kid_name, date, instrument } = data;
  const instrumentIcon = kidInstrumentAssets[instrument];

  const [fadeInDone, setFadeInDone] = useState(false);

  const logoSpring = useSpring({
    from: { opacity: 0, transform: 'translateY(20px)' },
    to: { opacity: 1, transform: 'translateY(0px)' },
    delay: 200,
  });

  const kidNameSpring = useSpring({
    from: { opacity: 0, transform: 'translateY(20px)' },
    to: { opacity: 1, transform: 'translateY(0px)' },
    delay: 200,
  });

  const titleSpring = useSpring({
    from: { opacity: 0, transform: 'translateX(100px)' },
    to: { opacity: 1, transform: 'translateX(0px)' },
    delay: 400,
    config: {
      tension: 180,  // lower tension = more wobble
      friction: 12,  // lower friction = 'less damping'
    },
  });

  const dateSpring = useSpring({
    from: { opacity: 0, transform: 'translateX(-100px)' },
    to: { opacity: 1, transform: 'translateX(0px)' },
    delay: 600,
    config: {
      tension: 180,
      friction: 12,
    },
  });

  const instrumentSpring = useSpring({
    from: { opacity: 0.5, transform: 'scale(0)' },
    to: { opacity: 1, transform: 'scale(1)' },
    delay: 1200,
    config: {
      tension: 180,
      friction: 12,
    },
  });

  const boxSpring = useSpring({
    from: { opacity: 0, transform: 'translateY(100vh)' },
    to: { opacity: 1, transform: 'translateY(0px)' },
    delay: 800,
  });

  const sunSpring = useSpring({
    from: { opacity: 0, transform: 'translateY(100vh)' },
    to: { opacity: 1, transform: 'translateY(0px)' },
    delay: 1000,
  });

  const sunRaysFadeInSpring = useSpring({
    from: { opacity: 0 },
    to: { opacity: 1 },
    config: { duration: 1000 },
    delay: 1000,
    onRest: () => setFadeInDone(true),
  });

  const sunRaysScaleSpring = useSpring({
    from: { scale: 1 },
    to: { scale: 1.1 },
    loop: fadeInDone ? { reverse: true } : false,
    config: { duration: 1500, easing: easings.easeInOutSine },
  });

  const sunRaysSpring = {
    ...sunRaysFadeInSpring,
    ...sunRaysScaleSpring,
  };

  const buttonSpring = useSpring({
    from: { opacity: 0, transform: 'scale(0.5)' },
    to: { opacity: 1, transform: 'scale(1)' },
    delay: 1400,
    config: { duration: 500 },
  });

  return (
    <div className='Slide__intro'>
      <animated.div className='Slide__intro__logo' style={logoSpring}>
        <ReactSVG src={logo} />
      </animated.div>
      <animated.h1 className='Slide__intro__kidName' style={kidNameSpring}>
        <Textfit mode="single" max={46} forceSingleModeWidth={false} style={{ maxWidth: '247px' }}>
          {kid_name}
          ’s
        </Textfit>
      </animated.h1>
      <animated.h2 className='Slide__intro__title' style={titleSpring}>Rocksteady Round-Up!</animated.h2>
      <animated.h3 className='Slide__intro__date' style={dateSpring}>{date}</animated.h3>
      <animated.div className='Slide__intro__instrument' style={instrumentSpring}>
        <ReactSVG src={instrumentIcon} />
      </animated.div>
      <animated.button className='Slide__intro__start' style={buttonSpring} type='button' onClick={startFunc}>
        Tap to start
      </animated.button>
      <animated.div className='Slide__intro__box' style={boxSpring} />
      <animated.div className='Slide__intro__sun' style={sunSpring} />
      <animated.div className='Slide__intro__sunRays' style={sunRaysSpring}>
        <ReactSVG src={sunRays} />
      </animated.div>
    </div>
  );
};

export default IntroSlide;

const DataProps = {
  kid_name: PropTypes.string.isRequired,
  instrument: PropTypes.string.isRequired,
  date: PropTypes.string.isRequired,
};

IntroSlide.propTypes = {
  data: PropTypes.shape(DataProps).isRequired,
  startFunc: PropTypes.func.isRequired,
};
